<template>
  <div
    v-if="!useDesktopLayout"
    class="mobile-menu-control"
    @click="toggleMobileMenu"
  >
    <v-icon icon="mdi-menu" />
  </div>
  <div
    ref="main-menu"
    class="menu"
  >
    <ShareDialog
      v-if="mapId"
      :map-id="mapId"
      :show-dialog="showShareDialog"
      :is-map-view="true"
      @close="handleShareDialogClose"
    />
    <div
      id="menu-bar"
      ref="menu-bar"
      class="menu-bar"
      :class="{
        'menu-bar-expanded': mobileMenuExpanded
      }"
    >
      <!-- Render menu icons -->
      <aside
        v-for="menu in filteredSubmenus"
        :id="`menu-item__${menu.id}`"
        :key="`menu-item__${menu.id}`"
        class="menu-item"
        :class="{
          'menu-item-selected': menu.id === selectedMenuItem,
          'menu-item-disabled': menu?.clickable === false,
          'menu-item-anchor-bottom': useDesktopLayout && anchorMenuItem(menu.id)
        }"
        @click="handleMenuItemClick(menu)"
      >
        <div
          v-if="menu.notifications"
          class="notification"
        >
          <v-avatar
            size="x-small"
            class="notification-icon"
          >
            {{ getNotificationCount(menu) }}
          </v-avatar>
        </div>

        <component
          :is="menu.id + `${menu.id === 'OrangeCircle' ? '' : 'Icon'}`"
          :id="menu.id"
          class="menu-svg"
        />
        <div
          v-if="menu.tip"
          class="menu-item__label"
          :class="{
            'menu-item__label-selected': menu.id === selectedMenuItem
          }"
        >
          {{ menu.tip }}
        </div>
        <!-- Display a bar next to the Overlay menu icon when an Overlay is selected -->
        <div
          v-if="selectedOverlay && menu.id === 'overlays'"
          class="selectedOverlayIndicator"
        />
      </aside>
    </div>

    <transition
      v-for="menu in subMenus"
      :key="`submenu-transition__${menu.id}`"
      name="slideRight"
    >
      <!-- LayersMenu uses v-if to mount on page load, ensuring persisted layers are displayed -->
      <keep-alive :include="['LayersMenu','ReportsMenu', 'OverlaysMenu', 'ToolboxMenu']">
        <component
          :is="menu.component"
          v-if="menu.type !== 'dialog' && (menu.id === selectedMenuItem || menu.id === 'layers')"
          v-show="menu.id === selectedMenuItem"
          :class="{
            'submenu-bar': menu.type === 'bar',
            'submenu-sheet': menu.type === 'sheet'
          }"
          :data-testid="`submenu-component__${menu.id}`"
          data-covers-map
          @close="setSelectedMenuItem(null)"
        />
      </keep-alive>
    </transition>

    <template
      v-for="menu in subMenus"
      :key="`submenu-dialog__${menu.id}`"
    >
      <component
        :is="menu.component"
        v-if="menu.type === 'dialog' && menu.id === selectedMenuItem"
        :data-testid="`submenu-component__${menu.id}`"
        @close="setSelectedMenuItem(null)"
      />
    </template>
  </div>
</template>

<script lang="ts">
// Services and Helpers
import {mapState, mapMutations, mapActions} from 'vuex';
import {useDesktopLayout} from '@/lib/ReusableFunctions/User';
import {MENU_BAR_WIDTH_PIXELS} from '@/constants/MainMenu';
import {ConfigService} from '@/services/ConfigService';
import type {MenuItem} from '@/store/baseIndex';
import type {InternalAppState} from '@/internal/store';

// Components
import FilterIcon from './svgs/MenuIcons/FilterIcon.vue';
import ExportIcon from './svgs/MenuIcons/ExportIcon.vue';
import SearchIcon from './svgs/MenuIcons/SearchIcon.vue';
import LegendIcon from './svgs/MenuIcons/LegendIcon.vue';
import ReportsIcon from './svgs/MenuIcons/ReportsIcon.vue';
import LayersIcon from './svgs/MenuIcons/LayersIcon.vue';
import OverlaysIcon from './svgs/MenuIcons/OverlaysIcon.vue';
import OmIcon from './svgs/MenuIcons/OmIcon.vue';
import UploadIcon from './svgs/MenuIcons/UploadIcon.vue';
import InfoIcon from './svgs/MenuIcons/InfoIcon.vue';
import ToolboxIcon from './svgs/MenuIcons/ToolboxIcon.vue';
import AddPropertyIcon from './svgs/MenuIcons/AddPropertyIcon.vue';
import TrashIcon from './svgs/MenuIcons/TrashIcon.vue';
import OrangeCircle from './svgs/MarkerIcons/Circles/OrangeCircle.vue';
import DashboardIcon from './svgs/MenuIcons/DashboardIcon.vue';
import NPEditorShareIcon from './svgs/MenuIcons/NPEditorShareIcon.vue';
import NPViewerShareIcon from './svgs/MenuIcons/NPViewerShareIcon.vue';
import ShareDialog from '@/external/components/ShareSettings/ShareDialog.vue';
// Right now, the shared item menu icon is the same as the overlays menu icon
import SharedItemMenuIcon from './svgs/MenuIcons/OverlaysIcon.vue';
import {getReadyForReviewOMs} from '@/services/OMService';

type MenuClickListener = (this: HTMLDivElement, ev: MouseEvent) => void;

export default {
  name: 'MainMenu',
  components: {
    SearchIcon,
    FilterIcon,
    ExportIcon,
    LegendIcon,
    ReportsIcon,
    LayersIcon,
    OverlaysIcon,
    OmIcon,
    UploadIcon,
    InfoIcon,
    ToolboxIcon,
    AddPropertyIcon,
    SharedItemMenuIcon,
    OrangeCircle,
    TrashIcon,
    DashboardIcon,
    NPEditorShareIcon,
    NPViewerShareIcon,
    ShareDialog,
  },
  provide() {
    return {
      addMenuClickListener: (name: string, callback: MenuClickListener) => this.addMenuClickListener(name, callback),
      removeMenuClickListener: (name: string) => this.removeMenuClickListener(name),
    };
  },
  data() {
    return {
      listeners: {} as Record<string, MenuClickListener>,
      showShareDialog: false,
      // Must be the literal string of pixels for v-bind to work
      menuBarWidthPixels: `${MENU_BAR_WIDTH_PIXELS}px`,
      menuItemHeightPixels: '55px',
      // Mobile-specific data
      mobileMenuHeightPixels: null as string | null,
      mobileMenuExpanded: false,
      // Data for layout control
      useDesktopLayout: useDesktopLayout(),
    };
  },
  computed: {
    ...mapState({
      selectedMenuItem: (state: any): InternalAppState['selectedMenuItem'] => (state as InternalAppState).selectedMenuItem,
      selectedOverlay: (state: any): InternalAppState['selectedOverlay'] => (state as InternalAppState).selectedOverlay,
      subMenus: (state: any): InternalAppState['subMenus'] => (state as InternalAppState).subMenus,
      mapId: (state: any): string => state.mapId,
    }),
    filteredSubmenus() {
      if (ConfigService.userIsOnTeam('acquisition')) {
        return this.subMenus;
      }
      // Bulk Upload is acquisition-only for v.1, same as the OM shelf.
      return (this.subMenus).filter((menu) => menu.id !== 'om' && menu.id !== 'upload');
    },
    omNotificationCount() {
      return getReadyForReviewOMs().length;
    },
  },
  watch: {
    async mobileMenuExpanded() {
      if (!this.mobileMenuHeightPixels && this.mobileMenuExpanded) {
        // Awaiting next tick is necessary to ensure the height is calculated after the menu is expanded
        await this.$nextTick();
        this.mobileMenuHeightPixels = `${(this.$refs['menu-bar'] as HTMLDivElement).offsetHeight}px`;
      }
    },
  },
  mounted() {
    if (process.env.VUE_APP_FLAVOR === 'external' && !useDesktopLayout()) {
      this.mobileMenuExpanded = true;
    }
  },
  methods: {
    ...mapMutations(['setSelectedMenuItem']),
    ...mapActions('infowindow', ['closeInfoWindow']),
    handleMenuItemClick(menuItem: MenuItem) {
      const clickedItemId = menuItem.id;
      if (['NPViewerShare', 'NPEditorShare'].includes(clickedItemId)) {
        this.showShareDialog = true;
      } else if (clickedItemId === 'dashboard') {
        this.closeInfoWindow();
        this.$router.push({name: 'DashboardView'});
        return;
      }

      this.setSelectedMenuItem(clickedItemId === this.selectedMenuItem ? null : clickedItemId);
    },
    getNotificationCount(menuItem: MenuItem) {
      let count: number | null = null;
      switch (menuItem.id) {
        case 'om': {
          count = this.omNotificationCount;
          break;
        }
      }

      if (count === null || !menuItem.notifications) {
        console.warn(`Could not get notification count for menu item "${menuItem.id}". Item was missing its notification config or the count itself was unusable.`);
        return;
      }
      if (menuItem.notifications.limit !== undefined) {
        return count > menuItem.notifications.limit ? `${menuItem.notifications.limit}+` : count;
      }
      return count;
    },
    async toggleMobileMenu() {
      this.mobileMenuExpanded = !this.mobileMenuExpanded;

      if (!this.mobileMenuExpanded && this.selectedMenuItem) {
        this.setSelectedMenuItem(null);
      }
    },
    addMenuClickListener(name: string, callback: MenuClickListener) {
      if (this.listeners[name]) {
        this.removeMenuClickListener(name);
      }
      (this.$refs['main-menu'] as HTMLDivElement).addEventListener('click', callback);
      this.listeners[name] = callback;
    },
    removeMenuClickListener(name: string) {
      if (this.$refs['main-menu']) {
        (this.$refs['main-menu'] as HTMLDivElement).removeEventListener('click', this.listeners[name]);
        delete this.listeners[name];
      }
    },
    handleShareDialogClose() {
      this.showShareDialog = false;
      this.setSelectedMenuItem(null);
    },
    anchorMenuItem(itemId: string) {
      if (['trash', 'info', 'NPViewerShare'].includes(itemId)) return true;
      const allMenuItemIds = this.subMenus.map(({id}) => id);
      // Don't apply the anchoring CSS to multiple menu items, or else they will be anchored, but with a gap in-between.
      // Ideally, the CSS could be applied without caring about other menu items.
      if (!allMenuItemIds.includes('NPViewerShare') && itemId === 'OrangeCircle') return true;
      return false;
    },
  },
};
</script>

<style lang="scss"> // eslint-disable-line vue-scoped-css/enforce-style-type
// Hide Google Maps controls and footer on mobile
.gmnoprint {
  display: none;
  @include main.respond(desktop) {
    display: block;
  }
}
</style>

<!-- eslint-disable vue-scoped-css/no-parsing-error -->
<style lang="scss" scoped>
// <transition> specific properties
.slideRight-enter-active,
.slideRight-leave-active {
  transition: all 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);
}
.slideRight-enter-from,
.slideRight-leave-to {
  transform: translateX(-350px);
}

// <template> styling
.submenu-sheet {
  background-color: #FFF;
  height: calc(v-bind(mobileMenuHeightPixels) + v-bind(menuItemHeightPixels));
  width: calc(100vw - v-bind(menuBarWidthPixels));
  position: absolute;
  top: calc(v-bind(menuItemHeightPixels) * -1);
  left: v-bind(menuBarWidthPixels);
  display: flex;
  flex-direction: column;
  z-index: 1 !important;
  padding: min(1vh, 20px) min(3vw, 25px) min(1vh, 20px) min(3vw, 25px);
  text-align: left;
  color: main.$npGray;
  font-size: 15px;
  overflow-y: overlay;
  overflow-x: hidden;

  @include main.respond(desktop) {
    top: 0;
    width: 350px;
    height: 100%;
    padding: unset;
  }
}

.selectedOverlayIndicator {
  position: absolute;
  background-color: main.$npOrange;
  height: v-bind(menuItemHeightPixels);
  left: calc(100% - 4px);
  width: 4px;
  z-index: 999;
}

.mobile-menu-control {
  background-color: #FFF;
  width: v-bind(menuBarWidthPixels);
  height: v-bind(menuItemHeightPixels);
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 15;
  box-shadow: -2px 0px 8px 1px rgba(0, 0, 0, 0.3);
}

.submenu-bar {
  position: absolute;
  left: calc(v-bind(menuBarWidthPixels) + 3vw);
  // Take up as much space as possible while leaving the same gap at the right edge as is on the left.
  width: calc(100vw - (v-bind(menuBarWidthPixels) + 3vw) - 3vw);
  // height defaults to the height of the menu bar - it can be overridden at the component level.
  // It is mostly used because max-height does not work without a height property.
  height: v-bind(mobileMenuHeightPixels);
  max-height: v-bind(mobileMenuHeightPixels);
  z-index: 1 !important;
  border-radius: 5px;

  @include main.respond(desktop) {
    width: 300px;
    left: calc(v-bind(menuBarWidthPixels) + 15px);
  }
}

.menu-svg {
  fill: main.$npGray;
  transform: scale(1.2);
  :deep(line) {
    stroke: main.$npGray;
  }
}

.menu {
  position: absolute;
  top: v-bind(menuItemHeightPixels);
  width: v-bind(menuBarWidthPixels);
  height: v-bind(mobileMenuHeightPixels);
  z-index: 10 !important;

  &-bar {
    display: none;
    position: absolute;
    width: v-bind(menuBarWidthPixels);
    background-color: #FFF;

    &-expanded {
      display: block;
      height: auto;
      z-index: 10 !important;
      box-shadow: -2px 0px 8px 1px rgba(0, 0, 0, 0.3);
    }
  }

  &-item {
    position: relative;
    width: 100%;
    height: v-bind(menuItemHeightPixels);
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    cursor: pointer;

    .notification {
      position: absolute;
      top: -4px;
      right: 7px;

      &-icon {
        z-index: 2;
        font-size: 14px;
        font-weight: 600;
        background-color: main.$red;
        color: white;
        height: 20px;
        width: 20px;
      }
    }

    &:hover {
      background-color: main.$midGray;
    }
    &-selected, &-selected:hover {
      background-color: main.$npGray;
      .menu-svg {
        fill: #FFF;
        :deep(.st1) {
          fill: #FFF;
        }
        :deep(line) {
          stroke: #FFF;
        }
      }
    }
    &__label {
      padding-top: 3px;
      font-size: 12px;
      font-weight: 600;
      &-selected {
        color: #FFF;
        font-weight: bold;
      }
    }
    &-disabled, &-disabled:hover {
      background-color: transparent;
      pointer-events: none;
      cursor: default;
    }
    &-anchor-bottom {
      margin-top: auto;
    }
  }
  @include main.respond(desktop) {
    height: 100%;
    top: 0;
    z-index: 5 !important;

    &-bar {
      height: 100%;
      position: absolute;
      display: flex;
      justify-content: flex-start;
      flex-direction: column;
      padding-top: 20px;
      z-index: 10 !important;
      box-shadow: -2px 0px 8px 1px rgba(0, 0, 0, 0.3);
    }
    .submenu-bar {
      top: 20px;
    }
  }
}
</style>
